import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withStyles } from '@material-ui/core/styles';
import get from 'lodash.get';
import {
    Typography,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Chip,
    Grid,
} from '@material-ui/core';
import CancelPresentation from '@material-ui/icons/CancelPresentation';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationConfusionModel,
    selectApplicationConfusionReaction,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormSelect from 'Console/components/common/form/FormSelect';
import FormInput from 'Console/components/common/form/FormInput';
import FormItemSwitch from 'Console/components/common/form/FormItemSwitch';
import GuideGeneration from 'Console/components/applications/detail/settings/confusion/GuideGeneration';

import { MODEL_TYPE_CONFUSION, CONFUSION_REACTION } from 'Console/constants/appConstants';
import chemicalBank from 'Console/assets/images/tool.svg';

const styles = (theme) => ({
    root: {
        flexGrow: 1,
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        fontWeight: 'bold',
        paddingBottom: theme.typography.pxToRem(20),
        paddingRight: theme.typography.pxToRem(10),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        paddingBottom: theme.typography.pxToRem(25),
    },
    spaceBottom: {
        paddingBottom: theme.typography.pxToRem(30),
    },
    btn: {
        marginTop: theme.typography.pxToRem(20),
        marginBottom: theme.typography.pxToRem(40),
    },
    btnAdd: {
        marginTop: theme.typography.pxToRem(15),
        marginLeft: theme.typography.pxToRem(47),
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
        },
    },
    listItems: {
        paddingTop: theme.typography.pxToRem(20),
        paddingBottom: theme.typography.pxToRem(20),
        width: '100%',
    },
    listItem: {
        padding: 0,
    },
    boxList: {
        width: '43%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    subTitleActive: {
        paddingRight: theme.typography.pxToRem(18),
        [theme.breakpoints.between('xs', 'sm')]: {
            paddingRight: 0,
            paddingTop: theme.typography.pxToRem(10),
        },
    },
    formInput: {
        width: '33%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
            marginBottom: '5%',
        },
    },
    experimental: {
        fontSize: theme.typography.pxToRem(10),
    },
    img: {
        display: 'inline-block',
        width: theme.typography.pxToRem(8),
        marginLeft: theme.typography.pxToRem(8),
    },
});

const Confusion = ({
    classes,
    intl,
    confusionModel,
    confusionReaction,
    form,
    form: { getFieldDecorator, setFieldsValue, getFieldValue },
    updateDetailSettings,
}) => {
    const confusionReactionsOptions = CONFUSION_REACTION.map(({ value, id, defaultMessage }) => ({
        value,
        name: intl.formatMessage({ id, defaultMessage }),
    }));

    const handleSaveSettings = () => {
        const formValues = getFieldValue('confusion');
        const arrayUrls = getFieldValue('arrayUrls');
        const reactionValues = getFieldValue('reaction');

        updateDetailSettings(
            {
                'metadata.tracking.model.enabled': formValues.enabled,
                'metadata.tracking.model.type': formValues.confusionModel.value,
                'metadata.tracking.model.id': formValues.confusionModelId,
                'metadata.tracking.model.urls': arrayUrls,
                'metadata.tracking.model.confusionTimeoutSeconds': parseInt(
                    formValues.confusionTimeoutSeconds,
                    10
                ),
                'metadata.tracking.confusion.reaction.type': reactionValues.type.value,
                'metadata.tracking.confusion.reaction.numberOfShakes': parseInt(
                    reactionValues.numberOfShakes,
                    10
                ),
            },
            {
                success: (
                    <FormattedMessage id="Notification.save" defaultMessage="Your settings were saved" />
                ),

                fail: (
                    <FormattedMessage
                        id="Notification.fail"
                        defaultMessage="Unable to save, something went wrong"
                    />
                ),
            }
        );
    };

    const handleAddRow = () => {
        const url = getFieldValue('confusion.confusionModelUrls');
        const arrayUrls = getFieldValue('arrayUrls');

        setFieldsValue({
            arrayUrls: [...arrayUrls, url],
            'confusion.confusionModelUrls': '',
        });
    };

    const handleRemoveItem = (itemToDelete) => {
        const arrayUrls = getFieldValue('arrayUrls');

        setFieldsValue({
            arrayUrls: arrayUrls.filter((item) => item !== itemToDelete),
        });
    };

    getFieldDecorator('confusion.enabled', {
        initialValue: get(confusionModel, 'enabled'),
        preserve: true,
    });

    getFieldDecorator('confusion.confusionModel', {
        initialValue: MODEL_TYPE_CONFUSION.find(
            (option) => option.value === get(confusionModel, 'type')
        ),
        preserve: true,
    });

    getFieldDecorator('confusion.confusionTimeoutSeconds', {
        initialValue: get(confusionModel, 'confusionTimeoutSeconds'),
        preserve: true,
    });

    getFieldDecorator('arrayUrls', {
        initialValue: get(confusionModel, 'urls'),
        preserve: true,
    });

    getFieldDecorator('reaction.type', {
        initialValue: CONFUSION_REACTION.find(
            (option) => option.value === get(confusionReaction, 'type')
        ),
        preserve: true,
    });

    getFieldDecorator('reaction.numberOfShakes', {
        initialValue: get(confusionReaction, 'numberOfShakes'),
        preserve: true,
    });

    return (
        <Grid className={classes.root}>
            <Grid>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage
                            id="Settings.confusion.title"
                            defaultMessage="Predict the confusion of users"
                        />
                    </Typography>

                    <Chip
                        label={intl.formatMessage({ id: 'Settings.tracking.experimental' })}
                        icon={<img className={classes.img} alt="logo" src={chemicalBank} />}
                        className={classes.experimental}
                    />
                </Grid>

                <Grid container direction="row" alignItems="center" className={classes.spaceBottom}>
                    <Typography variant="body1" className={classes.subTitleActive}>
                        <FormattedMessage
                            id="Buttons.switchConfusionEnable"
                            defaultMessage="Enable confusion model"
                        />
                    </Typography>

                    <FormItemSwitch
                        initialValue={get(confusionModel, 'enabled')}
                        form={form}
                        formItemKey="confusion.enabled"
                    />
                </Grid>
            </Grid>
            {getFieldValue('confusion.enabled') && (
                <>
                    <Grid className={classes.formInput} style={{ paddingBottom: '20px' }}>
                        <FormSelect
                            form={form}
                            fullWidth={false}
                            isRequired
                            label={intl.formatMessage({ id: 'Settings.confusion.label' })}
                            formItemKey="confusion.confusionModel"
                            initialValue={MODEL_TYPE_CONFUSION.find(
                                (option) => option.value === get(confusionModel, 'type')
                            )}
                            optionList={MODEL_TYPE_CONFUSION}
                        />
                    </Grid>

                    {getFieldValue('confusion.confusionModel') &&
                        getFieldValue('confusion.confusionModel').value === 'detection' && (
                            <Grid container direction="column" style={{ paddingBottom: '20px' }}>
                                <Typography variant="subtitle1">
                                    <FormattedMessage
                                        id="Settings.confusion.modelId.label"
                                        defaultMessage="Model ID"
                                    />
                                </Typography>

                                <Grid className={classes.formInput}>
                                    <FormInput
                                        initialValue={get(confusionModel, 'id')}
                                        fullWidth={false}
                                        form={form}
                                        type="text"
                                        formItemKey="confusion.confusionModelId"
                                    />
                                </Grid>
                            </Grid>
                        )}

                    {getFieldValue('confusion.confusionModel') &&
                        getFieldValue('confusion.confusionModel').value === 'heurestic' && (
                            <>
                                <Grid container direction="row" alignItems="flex-end">
                                    <Grid className={classes.formInput}>
                                        <FormInput
                                            fullWidth={false}
                                            form={form}
                                            type="text"
                                            formItemKey="confusion.confusionModelUrls"
                                            placeholder={intl.formatMessage({
                                                id: 'Settings.confusion.modelUrls.placeholder',
                                            })}
                                        />
                                    </Grid>

                                    <Typography variant="button">
                                        <Button
                                            id="yeself-button"
                                            variant="contained"
                                            color="primary"
                                            onClick={handleAddRow}
                                            className={classes.btnAdd}
                                            disabled={!getFieldValue('confusion.confusionModelUrls')}
                                        >
                                            <FormattedMessage id="Buttons.adde" defaultMessage="Add" />
                                        </Button>
                                    </Typography>
                                </Grid>

                                <Grid container className={classes.boxList}>
                                    <List className={classes.listItems}>
                                        {getFieldValue('arrayUrls').map((item) => (
                                            <ListItem key={item} className={classes.listItem}>
                                                <ListItemText value={item} primary={item} />

                                                <ListItemIcon>
                                                    <IconButton
                                                        className={classes.deleteBtn}
                                                        aria-label="Delete"
                                                        onClick={() => handleRemoveItem(item)}
                                                    >
                                                        <CancelPresentation />
                                                    </IconButton>
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            </>
                        )}

                    <Grid container direction="column" style={{ paddingBottom: '20px' }}>
                        <Typography variant="subtitle1">
                            <FormattedMessage
                                id="Settings.confusion.confusionTimeoutSeconds"
                                defaultMessage="Confusion timeout (seconds)"
                            />
                        </Typography>

                        <Grid className={classes.formInput}>
                            <FormInput
                                fullWidth={false}
                                form={form}
                                type="number"
                                formItemKey="confusion.confusionTimeoutSeconds"
                                initialValue={get(confusionModel, 'confusionTimeoutSeconds')}
                            />
                        </Grid>
                    </Grid>

                    <Grid className={classes.formInput} style={{ paddingBottom: '20px' }}>
                        <FormSelect
                            form={form}
                            fullWidth={false}
                            isRequired
                            label={intl.formatMessage({ id: 'Settings.confusion.reaction.label' })}
                            formItemKey="reaction.type"
                            initialValue={confusionReactionsOptions.find(
                                (option) => option.value === get(confusionReaction, 'type')
                            )}
                            optionList={confusionReactionsOptions}
                        />
                    </Grid>

                    {getFieldValue('reaction.type') &&
                        getFieldValue('reaction.type').value === 'shakeContextHelp' && (
                            <Grid container direction="column" style={{ paddingBottom: '20px' }}>
                                <Typography variant="subtitle1">
                                    <FormattedMessage
                                        id="Settings.confusion.reaction.numberOfShakes"
                                        defaultMessage="Number of shakes"
                                    />
                                </Typography>

                                <Grid className={classes.formInput}>
                                    <FormInput
                                        fullWidth={false}
                                        form={form}
                                        type="number"
                                        formItemKey="reaction.numberOfShakes"
                                        initialValue={get(confusionReaction, 'numberOfShakes')}
                                    />
                                </Grid>
                            </Grid>
                        )}
                </>
            )}
            <Grid container alignItems="center">
                <Typography variant="button">
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveSettings}
                        className={classes.btn}
                    >
                        <FormattedMessage id="Buttons.save" defaultMessage="Save" />
                    </Button>
                </Typography>
            </Grid>

            <GuideGeneration />
        </Grid>
    );
};

export default connect(
    (state) => ({
        confusionModel: selectApplicationConfusionModel(state),
        confusionReaction: selectApplicationConfusionReaction(state),
    }),
    { updateDetailSettings }
)(withStyles(styles)(injectIntl(Confusion)));
